<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { FileText, Plus, Pencil, ChevronUp, ChevronDown, X } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { termsTemplatesApi } from '$lib/api/terms';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { TERMS_CATEGORIES, type QuotationTerm } from '$lib/types';

  const {
    quotationId,
    terms,
    vertical,
    editable
  }: { quotationId: number; terms: QuotationTerm[]; vertical: string; editable: boolean } = $props();

  const queryClient = useQueryClient();
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] });
  }

  // Only fetched when the "add from template" picker is actually open —
  // no point loading the template list for quotes nobody is editing.
  let showTemplatePicker = $state(false);
  const templatesQuery = createQuery(() => ({
    queryKey: ['terms-templates', vertical],
    queryFn: () => termsTemplatesApi.list(vertical),
    enabled: showTemplatePicker
  }));

  const applyMut = createMutation(() => ({
    mutationFn: (templateId: number) => quotationsApi.applyTermsTemplate(quotationId, templateId),
    onSuccess: () => {
      invalidate();
      showTemplatePicker = false;
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  let showCustomForm = $state(false);
  function emptyCustomForm() {
    return { category: TERMS_CATEGORIES[0] as string, title: '', body: '' };
  }
  let customForm = $state(emptyCustomForm());

  const createMut = createMutation(() => ({
    mutationFn: (body: { category: string; title: string; body: string }) => quotationsApi.createTerm(quotationId, body),
    onSuccess: () => {
      invalidate();
      showCustomForm = false;
      customForm = emptyCustomForm();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function submitCustom(e: SubmitEvent) {
    e.preventDefault();
    if (!customForm.title.trim()) return;
    createMut.mutate({ ...customForm });
  }

  let editingId = $state<number | null>(null);
  let editForm = $state({ category: '', title: '', body: '' });

  function startEdit(term: QuotationTerm) {
    editingId = term.id;
    editForm = { category: term.category, title: term.title, body: term.body };
  }

  const updateMut = createMutation(() => ({
    mutationFn: ({ termId, body }: { termId: number; body: Record<string, unknown> }) =>
      quotationsApi.updateTerm(quotationId, termId, body),
    onSuccess: () => {
      invalidate();
      editingId = null;
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function saveEdit(e: SubmitEvent) {
    e.preventDefault();
    if (editingId === null) return;
    updateMut.mutate({ termId: editingId, body: editForm });
  }

  const deleteMut = createMutation(() => ({
    mutationFn: (termId: number) => quotationsApi.deleteTerm(quotationId, termId),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const reorderMut = createMutation(() => ({
    mutationFn: (termIds: number[]) => quotationsApi.reorderTerms(quotationId, termIds),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= terms.length) return;
    const ids = terms.map((t) => t.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMut.mutate(ids);
  }
</script>

<div class="tym-card px-[22px] py-5">
  <div class="flex items-center gap-2 mb-3">
    <FileText class="h-4 w-4 text-toyama-teal" />
    <h3 class="font-heading text-[15px] font-bold text-navy">Terms &amp; Conditions</h3>
    <span class="flex-1"></span>
    {#if editable}
      <button type="button" class="btn-secondary !h-8 !text-xs" onclick={() => (showTemplatePicker = !showTemplatePicker)}>
        <Plus class="h-3.5 w-3.5" /> From template
      </button>
      <button type="button" class="btn-secondary !h-8 !text-xs" onclick={() => (showCustomForm = !showCustomForm)}>
        <Plus class="h-3.5 w-3.5" /> Custom
      </button>
    {/if}
  </div>

  {#if editable && showTemplatePicker}
    <div class="mb-3 rounded-md border border-toyama-cloudy-gray p-3">
      {#if templatesQuery.isLoading}
        <p class="tym-loading-state">Loading templates&hellip;</p>
      {:else if templatesQuery.isError}
        <p class="tym-error-state">{getErrorMessage(templatesQuery.error)}</p>
      {:else}
        <div class="flex flex-col gap-1.5">
          {#each templatesQuery.data?.results ?? [] as tmpl (tmpl.id)}
            <button
              type="button"
              class="text-left text-sm rounded-md px-2.5 py-1.5 hover:bg-gray-50 flex items-center gap-2"
              disabled={applyMut.isPending}
              onclick={() => applyMut.mutate(tmpl.id)}
            >
              <span class="tym-badge tym-badge-neutral shrink-0">{tmpl.category.replace('_', ' ')}</span>
              <span class="text-navy">{tmpl.name}</span>
            </button>
          {:else}
            <p class="tym-empty-state">No templates for this vertical.</p>
          {/each}
        </div>
      {/if}
    </div>
  {/if}

  {#if editable && showCustomForm}
    <form class="mb-3 flex flex-col gap-2 rounded-md border border-toyama-cloudy-gray p-3" onsubmit={submitCustom}>
      <div class="flex gap-2">
        <select class="input !h-9" bind:value={customForm.category}>
          {#each TERMS_CATEGORIES as c (c)}
            <option value={c}>{c.replace('_', ' ')}</option>
          {/each}
        </select>
        <input class="input !h-9 flex-1" placeholder="Title" bind:value={customForm.title} />
      </div>
      <textarea class="input min-h-[100px] font-mono text-xs" placeholder="Body" bind:value={customForm.body}
      ></textarea>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary !h-8 !text-xs" onclick={() => (showCustomForm = false)}>Cancel</button>
        <button type="submit" class="btn-primary !h-8 !text-xs" disabled={createMut.isPending}>Add term</button>
      </div>
    </form>
  {/if}

  {#if terms.length === 0}
    <p class="tym-empty-state">No terms attached yet.</p>
  {:else}
    <div class="flex flex-col gap-2">
      {#each terms as term, i (term.id)}
        <div class="rounded-md border border-toyama-cloudy-gray p-3">
          {#if editingId === term.id}
            <form class="flex flex-col gap-2" onsubmit={saveEdit}>
              <div class="flex gap-2">
                <select class="input !h-9" bind:value={editForm.category}>
                  {#each TERMS_CATEGORIES as c (c)}
                    <option value={c}>{c.replace('_', ' ')}</option>
                  {/each}
                </select>
                <input class="input !h-9 flex-1" bind:value={editForm.title} />
              </div>
              <textarea class="input min-h-[100px] font-mono text-xs" bind:value={editForm.body}></textarea>
              <div class="flex justify-end gap-2">
                <button type="button" class="btn-secondary !h-8 !text-xs" onclick={() => (editingId = null)}>
                  Cancel
                </button>
                <button type="submit" class="btn-primary !h-8 !text-xs" disabled={updateMut.isPending}>Save</button>
              </div>
            </form>
          {:else}
            <div class="flex items-start gap-2">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5 mb-1">
                  <span class="tym-badge tym-badge-neutral">{term.category.replace('_', ' ')}</span>
                  <span class="font-heading text-[13.5px] font-bold text-navy">{term.title}</span>
                </div>
                <pre class="tym-small whitespace-pre-wrap text-dark-gray">{term.body}</pre>
              </div>
              {#if editable}
                <div class="flex shrink-0 flex-col gap-1">
                  <div class="flex gap-1">
                    <button
                      type="button"
                      class="text-dark-gray hover:text-navy disabled:opacity-30"
                      disabled={i === 0 || reorderMut.isPending}
                      onclick={() => move(i, -1)}
                      aria-label="Move up"
                    >
                      <ChevronUp class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="text-dark-gray hover:text-navy disabled:opacity-30"
                      disabled={i === terms.length - 1 || reorderMut.isPending}
                      onclick={() => move(i, 1)}
                      aria-label="Move down"
                    >
                      <ChevronDown class="h-4 w-4" />
                    </button>
                  </div>
                  <div class="flex gap-1">
                    <button
                      type="button"
                      class="text-dark-gray hover:text-navy"
                      onclick={() => startEdit(term)}
                      aria-label="Edit term"
                    >
                      <Pencil class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="text-dark-gray hover:text-red-600"
                      onclick={() => deleteMut.mutate(term.id)}
                      aria-label="Delete term"
                    >
                      <X class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
